import {
  CIAMPermission,
  CIAMRole,
  CIAMSystemRoles,
  DecodedToken,
} from '@src/core/auth/components/types/accessToken';
import { Permissions } from '@src/core/auth/components/types/permissions.enum';
import { UserAccess } from '@src/core/auth/components/types/token-provider-context.interface';

export const DEFAULT_USER_ACCESS: UserAccess = {
  canUserAccessCSM: false,
  canUserAccessCSMCompany: false,
  canUserAccessAutomation: false,
  canUserAccessRequestsCatalog: false,
  canUserAccessReports: false,
  canUserAccessPublisherUpdates: false,
  canUserAccessPublisherUpdateTabMyContracts: false,
  canUserAccessMyProjects: false,
  canUserRaiseCase: false,
  isTenantAdmin: false,
  isPlatformAdmin: false,
  isPortalAdmin: false,
};

const getNormalizedForm = <T extends { name: string }>(
  nonNormalizedCollection: Array<T> | Array<string> = []
): Array<string> => {
  const [first] = nonNormalizedCollection;
  const isFirstActualString = typeof first === 'string';

  return isFirstActualString
    ? (nonNormalizedCollection as string[])
    : (nonNormalizedCollection as T[]).map((content) => content.name);
};

export const getCiamAccess = (token: DecodedToken) => {
  const ciamRoles = token.ciam_roles;
  const normalizedRoles = getNormalizedForm<CIAMRole>(ciamRoles);

  const isTenantAdmin =
    normalizedRoles.includes(CIAMSystemRoles.TenantAdmin) || DEFAULT_USER_ACCESS.isTenantAdmin;

  const isPlatformAdmin =
    normalizedRoles.includes(CIAMSystemRoles.PlatformAdmin) || DEFAULT_USER_ACCESS.isPlatformAdmin;

  const isPortalAdmin =
    normalizedRoles.includes(CIAMSystemRoles.PortalAdmin) || DEFAULT_USER_ACCESS.isPortalAdmin;

  return {
    isTenantAdmin,
    isPlatformAdmin,
    isPortalAdmin,
  };
};

export const getSupportSystemAccess = (token: DecodedToken) => {
  const normalizedPermissions = getNormalizedForm<CIAMPermission>(token.ciam_permissions);

  return {
    canUserAccessCSM: normalizedPermissions.includes(Permissions.ViewTickets),
    canUserAccessCSMCompany: normalizedPermissions.includes(Permissions.ViewCompanyTickets),
    canUserRaiseCase:
      normalizedPermissions.includes(Permissions.RaiseRequest) ||
      normalizedPermissions.includes(Permissions.RaiseIncident),
  };
};

export const getPublisherUpdatesAccess = (token: DecodedToken) => {
  const permissions = token.ciam_permissions;
  const normalizedPermissions = getNormalizedForm<CIAMPermission>(permissions);

  const canUserAccessPublisherUpdates =
    normalizedPermissions.includes(Permissions.ViewPublisherUpdates) ||
    DEFAULT_USER_ACCESS.canUserAccessPublisherUpdates;

  const canUserAccessPublisherUpdateTabMyContracts =
    normalizedPermissions.includes(Permissions.ViewPublisherUpdateContracts) ||
    DEFAULT_USER_ACCESS.canUserAccessPublisherUpdateTabMyContracts;

  return {
    canUserAccessPublisherUpdates,
    canUserAccessPublisherUpdateTabMyContracts,
  };
};

export const getReportsAccess = (token: DecodedToken) => {
  const normalizedPermissions = getNormalizedForm<CIAMPermission>(token.ciam_permissions);

  let { canUserAccessReports } = DEFAULT_USER_ACCESS;

  if (!token.ciam_user) {
    return {
      canUserAccessReports,
    };
  }

  canUserAccessReports = normalizedPermissions.includes(Permissions.ViewReports);

  return {
    canUserAccessReports,
  };
};

export const getRequestsCatalogAccess = (token: DecodedToken) => {
  const normalizedPermissions = getNormalizedForm<CIAMPermission>(token.ciam_permissions);

  let { canUserAccessRequestsCatalog } = DEFAULT_USER_ACCESS;

  if (!token.ciam_user) {
    return {
      canUserAccessRequestsCatalog,
    };
  }

  canUserAccessRequestsCatalog = normalizedPermissions.includes(Permissions.ViewRequests);

  return {
    canUserAccessRequestsCatalog,
  };
};

export const getAutomationAccess = (token: DecodedToken) => {
  const normalizedPermissions = getNormalizedForm<CIAMPermission>(token.ciam_permissions);

  let { canUserAccessAutomation } = DEFAULT_USER_ACCESS;

  if (!token.ciam_user) {
    return {
      canUserAccessAutomation,
    };
  }

  canUserAccessAutomation = normalizedPermissions.includes(Permissions.ViewAutomations);

  return {
    canUserAccessAutomation,
  };
};

export const getMyProjectsAccess = (token: DecodedToken) => {
  const permissions = token.ciam_permissions;
  const normalizedPermissions = getNormalizedForm<CIAMPermission>(permissions);

  const canUserAccessMyProjects =
    normalizedPermissions.includes(Permissions.ViewMyProjects) ||
    DEFAULT_USER_ACCESS.canUserAccessPublisherUpdates;

  return {
    canUserAccessMyProjects,
  };
};
